import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { PerformanceBrandPrintMenu } from "@/modules/performance/presentation/ui/PerformanceBrandPrintMenu";
import { isExpenseRecordPrintable } from "@/modules/expenses/domain/lib/expense-print";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CalendarIcon,
  FileTextIcon,
  HistoryIcon,
  PaperclipIcon,
  ReceiptIcon,
  TagsIcon,
  UserIcon,
  ZapIcon,
} from "lucide-react";
import { useState } from "react";
import { expensesApi } from "../api/client";
import {
  useApproveExpenseRecordMutation,
  useCancelExpenseRecordMutation,
  useCloseExpenseRecordMutation,
  useExpenseRecordQuery,
  useMarkExpensePaidMutation,
  useRejectExpenseRecordMutation,
  useSubmitExpenseRecordMutation,
  useVoidExpenseRecordMutation,
} from "../api/queries";
import { formatExpenseAmount } from "../lib/expense-format";
import { ExpenseAttachmentUploader } from "../ui/ExpenseAttachmentUploader";
import { ExpenseDetailField } from "../ui/ExpenseDetailField";
import { ExpenseRecordActions } from "../ui/ExpenseRecordActions";
import { ExpenseRecordStatusBadge } from "../ui/ExpenseRecordStatusBadge";
import { ExpenseRecordTimeline } from "../ui/ExpenseRecordTimeline";
import { MarkExpensePaidDialog } from "../ui/MarkExpensePaidDialog";
import { RejectExpenseDialog } from "../ui/RejectExpenseDialog";

export function ExpenseRecordDetailPage() {
  const { id } = useParams({ from: "/app/expenses/$id" });
  const nav = useNavigate({ from: "/app/expenses/$id" });
  const { data: record, isLoading } = useExpenseRecordQuery(id ?? null);

  const canCreate = useActionPermission(["expenses:create"]);
  const canUpdate = useActionPermission(["expenses:update"]);
  const canApprove = useActionPermission(["expenses:approve"]);
  const canPay = useActionPermission(["expenses:pay"]);
  const canVoid = useActionPermission(["expenses:void"]);

  const submitMutation = useSubmitExpenseRecordMutation(id ?? "");
  const approveMutation = useApproveExpenseRecordMutation(id ?? "");
  const rejectMutation = useRejectExpenseRecordMutation(id ?? "");
  const markPaidMutation = useMarkExpensePaidMutation(id ?? "");
  const closeMutation = useCloseExpenseRecordMutation(id ?? "");
  const cancelMutation = useCancelExpenseRecordMutation(id ?? "");
  const voidMutation = useVoidExpenseRecordMutation(id ?? "");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/expenses/overview" },
        { label: "ບັນທຶກຄ່າໃຊ້ຈ່າຍ", to: "/app/expenses" },
        { label: "ລາຍລະອຽດ" },
      ]}
    />
  );

  if (isLoading && !record) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <div className="flex items-center justify-center py-24">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (!record) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <div className="mx-auto max-w-lg py-16 text-center">
            <p className="text-muted-foreground">ບໍ່ພົບລາຍການ</p>
            <Button
              className="mt-4"
              variant="outline"
              onClick={() => nav({ to: "/app/expenses" })}
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const editable = ["draft", "rejected"].includes(record.status);
  const canUploadAttachment = canPay || (canUpdate && editable);
  const canDeleteAttachment = canUpdate && editable;

  return (
    <>
      <Header>{breadcrumb}</Header>
      <Main>
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-semibold text-2xl tracking-tight">
                  {record.number}
                </h1>
                <ExpenseRecordStatusBadge status={record.status} />
              </div>
              <p className="mt-1 text-muted-foreground text-sm">
                {record.categoryName} · {formatDate(record.expenseDate)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isExpenseRecordPrintable(record.status) ? (
                <PerformanceBrandPrintMenu
                  buildPrintUrl={(lang) =>
                    expensesApi.records.printUrl(record.id, lang)
                  }
                  label="ພິມໃບ"
                />
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/expenses" })}
              >
                <ArrowLeftIcon className="mr-2 size-4" />
                ກັບຄືນ
              </Button>
            </div>
          </div>

          <Card className="gap-0 overflow-hidden border-primary/20 bg-primary/5 py-0">
            <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-muted-foreground text-sm">ຈຳນວນຄ່າໃຊ້ຈ່າຍ</p>
                <p className="mt-1 font-semibold text-3xl tabular-nums tracking-tight">
                  {formatExpenseAmount(record.amount)}
                </p>
              </div>
              <div className="sm:text-right">
                <p className="font-medium">{record.payeeName}</p>
                <p className="text-muted-foreground text-sm">
                  {record.categoryName}
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <Card className="gap-0 py-0">
                <CardHeader className="space-y-1 border-b px-6 py-4">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ReceiptIcon className="size-4 text-muted-foreground" />
                    ຂໍ້ມູນຄ່າໃຊ້ຈ່າຍ
                  </CardTitle>
                  <CardDescription>
                    ອັບເດດລ່າສຸດ {formatDateTime(record.updatedAt)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="grid gap-5 px-6 py-5 sm:grid-cols-2">
                  <ExpenseDetailField label="ຜູ້ຮັບເງິນ / ຮ້ານ">
                    <span className="font-medium">{record.payeeName}</span>
                  </ExpenseDetailField>
                  <ExpenseDetailField label="ຫມວດ">
                    <span className="inline-flex items-center gap-1.5">
                      <TagsIcon className="size-3.5 text-muted-foreground" />
                      {record.categoryName}
                    </span>
                  </ExpenseDetailField>
                  <ExpenseDetailField label="ວັນທີຄ່າໃຊ້ຈ່າຍ">
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                      <CalendarIcon className="size-3.5 text-muted-foreground" />
                      {formatDate(record.expenseDate)}
                    </span>
                  </ExpenseDetailField>
                  <ExpenseDetailField label="ຜູ້ບັນທຶກ">
                    <span className="inline-flex items-center gap-1.5">
                      <UserIcon className="size-3.5 text-muted-foreground" />
                      {record.createdByName ?? record.createdBy}
                    </span>
                  </ExpenseDetailField>
                  {record.performanceEventTitle ? (
                    <ExpenseDetailField
                      label="ງານສະແດງ"
                      className="sm:col-span-2"
                    >
                      {record.performanceEventTitle}
                    </ExpenseDetailField>
                  ) : null}
                </CardContent>
              </Card>

              {(record.description || record.notes) && (
                <Card className="gap-0 py-0">
                  <CardHeader className="border-b px-6 py-4">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <FileTextIcon className="size-4 text-muted-foreground" />
                      ລາຍລະອຽດ ແລະ ຫມາຍເຫດ
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 px-6 py-5">
                    {record.description ? (
                      <ExpenseDetailField label="ລາຍລະອຽດ">
                        <div className="whitespace-pre-wrap rounded-lg border bg-muted/20 px-3 py-2.5">
                          {record.description}
                        </div>
                      </ExpenseDetailField>
                    ) : null}
                    {record.notes ? (
                      <ExpenseDetailField label="ຫມາຍເຫດ">
                        <div className="whitespace-pre-wrap rounded-lg border bg-muted/20 px-3 py-2.5">
                          {record.notes}
                        </div>
                      </ExpenseDetailField>
                    ) : null}
                  </CardContent>
                </Card>
              )}

              <Card className="gap-0 py-0">
                <CardHeader className="space-y-1 border-b px-6 py-4">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <PaperclipIcon className="size-4 text-muted-foreground" />
                    ໄຟລ໌ແນບ
                  </CardTitle>
                  <CardDescription>
                    {record.attachments.length > 0
                      ? `${record.attachments.length} ໄຟລ໌`
                      : "ຍັງບໍ່ມີໄຟລ໌ແນບ"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-6 py-5">
                  <ExpenseAttachmentUploader
                    expenseId={record.id}
                    attachments={record.attachments}
                    canUpload={!!canUploadAttachment}
                    canDelete={!!canDeleteAttachment}
                  />
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="gap-0 py-0 lg:sticky lg:top-6">
                <CardHeader className="border-b px-6 py-4">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ZapIcon className="size-4 text-muted-foreground" />
                    ການດຳເນີນການ
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-6 py-5">
                  <ExpenseRecordActions
                    record={record}
                    canCreate={!!canCreate}
                    canUpdate={!!canUpdate}
                    canApprove={!!canApprove}
                    canPay={!!canPay}
                    canVoid={!!canVoid}
                    isSubmitting={submitMutation.isPending}
                    isApproving={approveMutation.isPending}
                    isClosing={closeMutation.isPending}
                    isCancelling={cancelMutation.isPending}
                    onSubmit={() => submitMutation.mutate()}
                    onApprove={() => approveMutation.mutate()}
                    onReject={() => setRejectOpen(true)}
                    onMarkPaid={() => setMarkPaidOpen(true)}
                    onClose={() => closeMutation.mutate()}
                    onCancel={() => cancelMutation.mutate()}
                    onVoid={() => setVoidOpen(true)}
                  />
                </CardContent>
              </Card>

              <Card className="gap-0 py-0">
                <CardHeader className="border-b px-6 py-4">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <HistoryIcon className="size-4 text-muted-foreground" />
                    ປະຫວັດສະຖານະ
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-6 py-5">
                  <ExpenseRecordTimeline record={record} />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <RejectExpenseDialog
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          isPending={rejectMutation.isPending}
          onConfirm={async (rejectionReason) => {
            try {
              await rejectMutation.mutateAsync({ rejectionReason });
              setRejectOpen(false);
            } catch {
              toast.error("ປະຕິເສດລົ້ມເຫຼວ");
            }
          }}
        />

        <MarkExpensePaidDialog
          open={markPaidOpen}
          onOpenChange={setMarkPaidOpen}
          isPending={markPaidMutation.isPending}
          onConfirm={async (values) => {
            try {
              await markPaidMutation.mutateAsync(values);
              setMarkPaidOpen(false);
            } catch {
              toast.error("ບັນທຶກການຈ່າຍລົ້ມເຫຼວ");
            }
          }}
        />

        <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
          <DialogContent showCloseButton className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>ຍົກເລີກລາຍການ</DialogTitle>
              <DialogDescription>
                ລະບຸເຫດຜົນສຳລັບຍົກເລີກລາຍການທີ່ຈ່າຍ ຫຼື ປິດແລ້ວ
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="ເຫດຜົນຍົກເລີກ"
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              rows={3}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setVoidOpen(false)}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                disabled={!voidReason.trim() || voidMutation.isPending}
                onClick={async () => {
                  try {
                    await voidMutation.mutateAsync({ voidReason });
                    setVoidOpen(false);
                    setVoidReason("");
                  } catch {
                    toast.error("ຍົກເລີກລົ້ມເຫຼວ");
                  }
                }}
              >
                ຢືນຢັນຍົກເລີກ
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Main>
    </>
  );
}
